import React, { useEffect, useState } from "react";
import {
  MarkdownCell,
  CodeCell,
  Kw,
  Str,
  Cmt,
  OutputFigure,
} from "./Cell";
import FileTree from "./FileTree";
import {
  personalInfo,
  now,
  education,
  experience,
  fellowships,
  projects,
  publications,
  talks,
  ventures,
  awards,
  skills,
  HOME_FILE,
} from "../data/mock";

const featured = projects.filter((p) => p.featured);
const rest = projects.filter((p) => !p.featured);
const projectById = Object.fromEntries(projects.map((p) => [p.id, p]));
const paperById = Object.fromEntries(publications.map((p) => [p.id, p]));

const Notebook = ({ runNonce, onPulse, onTitle, filesOpen, onOpened }) => {
  const [file, setFile] = useState(HOME_FILE);
  const [sel, setSel] = useState("now");
  const [runningId, setRunningId] = useState(null);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    onTitle?.(file.name);
  }, [file, onTitle]);

  const openFile = (node) => {
    if (node.kind === "pdf") return;
    setFile(node);
    if (node.kind === "home") setSel("now");
    else if (node.kind === "project") setSel(`p-${node.id}`);
    else if (node.kind === "paper") setSel(`paper-${node.id}`);
    else if (node.kind === "talks") setSel("talks-md");
    onOpened?.();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const run = (id) => {
    onPulse?.();
    setSel(id);
    setRunningId(id);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.setTimeout(() => setRunningId(null), reduce ? 0 : 640);
  };

  useEffect(() => {
    if (!runNonce) return;
    run(sel);
  }, [runNonce]);

  const bind = (id, source) => ({
    selected: sel === id,
    onSelect: () => setSel(id),
    running: runningId === id,
    onRun: () => run(id),
    source: drafts[id] ?? source,
    onSource: (text) => setDrafts((d) => ({ ...d, [id]: text })),
  });

  return (
    <div className="nb">
      <aside className={`nb-rail${filesOpen ? " is-open" : ""}`}>
        <FileTree activeId={file.id} onOpen={openFile} />
      </aside>

      <div className="nb-sheet" id="nb-main">
        <div className="nb-tabs" role="tablist" aria-label="Open notebooks">
          <button
            type="button"
            role="tab"
            aria-selected={file.kind === "home"}
            className={`nb-tab${file.kind === "home" ? " is-active" : ""}`}
            onClick={() => openFile(HOME_FILE)}
          >
            {HOME_FILE.name}
          </button>
          {file.kind !== "home" && (
            <button
              type="button"
              role="tab"
              aria-selected
              className="nb-tab is-active"
            >
              {file.name}
              <span
                className="nb-tab-x"
                onClick={(e) => {
                  e.stopPropagation();
                  openFile(HOME_FILE);
                }}
              >
                ×
              </span>
            </button>
          )}
        </div>

        {file.kind === "project" && (
          <ProjectSheet
            project={projectById[file.id]}
            bind={bind}
            onOpen={openFile}
          />
        )}
        {file.kind === "paper" && (
          <PaperSheet paper={paperById[file.id]} bind={bind} onOpen={openFile} />
        )}
        {file.kind === "talks" && <TalksSheet bind={bind} />}
        {file.kind === "home" && <HomeSheet bind={bind} onOpen={openFile} />}
      </div>
    </div>
  );
};

function HomeSheet({ bind, onOpen }) {
  return (
    <>
      <MarkdownCell
        id="intro"
        {...bind(
          "intro",
          `${education.university} · ${education.degree} · expected ${education.expected}\n\n# ${personalInfo.name}\n\n${personalInfo.headline}`
        )}
      >
        <p className="md-meta">
          {education.university} · {education.degree} · expected {education.expected}
        </p>
        <h1>{personalInfo.name}</h1>
        <p className="lede">{personalInfo.headline}</p>
        <p>
          Computer engineering at Nebraska. Days go to polymer informatics
          and solid-state electrolytes; nights go to the things that have to
          work on a phone, a ring, or a database a chemist will actually
          search. I write the model and the interface around it.
        </p>
      </MarkdownCell>

      <CodeCell
        id="now"
        n={1}
        {...bind("now", "from lab import current\n\ncurrent()")}
        code={
          <>
            <Kw>from</Kw> lab <Kw>import</Kw> current
            {"\n\n"}
            current()
          </>
        }
      >
        <dl className="def">
          {now.roles.map((r) => (
            <div key={r.org} className="def-row">
              <dt>{r.title}</dt>
              <dd>
                {r.href ? (
                  <a href={r.href} target="_blank" rel="noreferrer">
                    {r.org}
                  </a>
                ) : (
                  r.org
                )}
                <span className="when">{r.when}</span>
                <p>{r.note}</p>
              </dd>
            </div>
          ))}
          <div className="def-row">
            <dt>latest paper</dt>
            <dd>
              <a href={now.latest.paper.href} target="_blank" rel="noreferrer">
                {now.latest.paper.title}
              </a>
              {" — "}
              {now.latest.paper.venue} ({now.latest.paper.role})
            </dd>
          </div>
          <div className="def-row">
            <dt>live tool</dt>
            <dd>
              <a href={now.latest.product.href} target="_blank" rel="noreferrer">
                {now.latest.product.title}
              </a>
              <span aria-hidden="true"> · </span>
              <button
                type="button"
                className="text-btn"
                onClick={() =>
                  onOpen({ id: "copolpred", name: "copolpred.ipynb", kind: "project" })
                }
              >
                open notebook
              </button>
            </dd>
          </div>
        </dl>
      </CodeCell>

      <CodeCell
        id="experience"
        n={2}
        {...bind(
          "experience",
          'appointments = lab.history(kind="research + company")\nappointments.table()'
        )}
        code={
          <>
            appointments = lab.history(kind=<Str>research + company</Str>)
            {"\n"}
            appointments.table()
          </>
        }
      >
        <ol className="jobs">
          {experience.map((job) => (
            <li key={job.org + job.when}>
              <header>
                <strong>{job.role}</strong>
                <span className="when">{job.when}</span>
              </header>
              <p className="org">
                {job.href ? (
                  <a href={job.href} target="_blank" rel="noreferrer">
                    {job.org}
                  </a>
                ) : (
                  job.org
                )}
                {job.where ? ` · ${job.where}` : ""}
              </p>
              <ul>
                {job.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        <ul className="grant-line">
          {fellowships.map((f) => (
            <li key={f.when}>
              <strong>{f.when}</strong> {f.title}, {f.org} — {f.detail}
            </li>
          ))}
        </ul>
      </CodeCell>

      <MarkdownCell
        id="experiments"
        {...bind("experiments", "## Selected experiments")}
      >
        <h2>Selected experiments</h2>
        <p>
          Five things I would hand you first. Open the file in the sidebar for
          the long form. Figures in the outputs are the live tools, or the
          hardware on the bench.
        </p>
      </MarkdownCell>

      {featured.map((p, i) => (
        <CodeCell
          key={p.id}
          id={p.id}
          n={3 + i}
          {...bind(
            p.id,
            `experiment("${p.id}", kind="${p.kind}")${
              p.href ? `\n# ${p.href.replace(/^https?:\/\//, "")}` : ""
            }`
          )}
          code={
            <>
              experiment(<Str>{p.id}</Str>, kind=<Str>{p.kind}</Str>)
              {p.href ? (
                <>
                  {"\n"}
                  <Cmt># {p.href.replace(/^https?:\/\//, "")}</Cmt>
                </>
              ) : null}
            </>
          }
        >
          {p.figure && (
            <OutputFigure
              src={p.figure}
              alt={p.title}
              caption={p.figureCaption}
              href={p.href}
            />
          )}
          <div className="exp">
            <h3>
              <button
                type="button"
                className="text-btn text-btn-title"
                onClick={() =>
                  onOpen({ id: p.id, name: `${p.id}.ipynb`, kind: "project" })
                }
              >
                {p.title}
              </button>
              <span className="kind">{p.kind}</span>
            </h3>
            <p>{p.blurb}</p>
            {p.points.length > 0 && (
              <ul>
                {p.points.map((pt) => (
                  <li key={pt}>{pt}</li>
                ))}
              </ul>
            )}
            <p className="exp-links">
              {p.href && (
                <a href={p.href} target="_blank" rel="noreferrer">
                  {p.href.replace(/^https?:\/\//, "")}
                </a>
              )}
              {p.repo && (
                <a href={p.repo} target="_blank" rel="noreferrer">
                  repository
                </a>
              )}
              <button
                type="button"
                className="text-btn"
                onClick={() =>
                  onOpen({ id: p.id, name: `${p.id}.ipynb`, kind: "project" })
                }
              >
                projects/{p.id}.ipynb
              </button>
            </p>
          </div>
        </CodeCell>
      ))}

      <CodeCell
        id="index"
        n={8}
        {...bind("index", "lab.index(featured=False)")}
        code={
          <>
            lab.index(featured=<Kw>False</Kw>)
          </>
        }
      >
        <ul className="index-list">
          {rest.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="text-btn text-btn-title"
                onClick={() =>
                  onOpen({ id: p.id, name: `${p.id}.ipynb`, kind: "project" })
                }
              >
                {p.title}
              </button>
              <span className="kind">{p.kind}</span>
              <span>{p.blurb}</span>
              <span className="exp-links">
                {p.href && (
                  <a href={p.href} target="_blank" rel="noreferrer">
                    site
                  </a>
                )}
                {p.repo && (
                  <a href={p.repo} target="_blank" rel="noreferrer">
                    repo
                  </a>
                )}
                <button
                  type="button"
                  className="text-btn"
                  onClick={() =>
                    onOpen({ id: p.id, name: `${p.id}.ipynb`, kind: "project" })
                  }
                >
                  open
                </button>
              </span>
            </li>
          ))}
        </ul>
      </CodeCell>

      <MarkdownCell id="papers" {...bind("papers", "## Bibliography")}>
        <h2>Bibliography</h2>
        <ol className="bib">
          {publications.map((pub) => (
            <li key={pub.id}>
              {pub.authors}{" "}
              {pub.href ? (
                <a href={pub.href} target="_blank" rel="noreferrer">
                  {pub.title}
                </a>
              ) : (
                <cite>{pub.title}</cite>
              )}
              . <i>{pub.venue}</i>, {pub.year}.
              {pub.note ? ` ${pub.note}` : ""}
              <span className="exp-links">
                {pub.href && (
                  <a href={pub.href} target="_blank" rel="noreferrer">
                    preprint
                  </a>
                )}
                <button
                  type="button"
                  className="text-btn"
                  onClick={() =>
                    onOpen({ id: pub.id, name: pub.file, kind: "paper" })
                  }
                >
                  papers/{pub.file}
                </button>
              </span>
            </li>
          ))}
        </ol>
      </MarkdownCell>

      <MarkdownCell id="talks" {...bind("talks", "## Talks, posters, pitches")}>
        <h2>Talks, posters, pitches</h2>
        <p>
          <button
            type="button"
            className="text-btn"
            onClick={() => onOpen({ id: "talks", name: "talks.md", kind: "talks" })}
          >
            talks.md
          </button>
        </p>
        <TalkList />
      </MarkdownCell>

      <CodeCell
        id="honors"
        n={9}
        {...bind("honors", "honors = lab.awards()\nprint(honors)")}
        code={
          <>
            honors = lab.awards()
            {"\n"}
            <Kw>print</Kw>(honors)
          </>
        }
      >
        <ul className="honors">
          {awards.map((a) => (
            <li key={a.title + a.year}>
              <span className="when">{a.year}</span>
              <span>
                <strong>{a.title}</strong>
                {a.org ? ` — ${a.org}` : ""}
                {a.detail ? ` ${a.detail}` : ""}
              </span>
            </li>
          ))}
        </ul>
      </CodeCell>

      <CodeCell
        id="stack"
        n={10}
        {...bind("stack", "lab.stack()")}
        code={<>lab.stack()</>}
      >
        <dl className="stack">
          <div>
            <dt>languages</dt>
            <dd>{skills.programming.join(" · ")}</dd>
          </div>
          <div>
            <dt>learning</dt>
            <dd>{skills.ml.join(" · ")}</dd>
          </div>
          <div>
            <dt>science</dt>
            <dd>{skills.science.join(" · ")}</dd>
          </div>
          <div>
            <dt>metal + radio</dt>
            <dd>{skills.mobile.join(" · ")}</dd>
          </div>
          <div>
            <dt>web</dt>
            <dd>{skills.web.join(" · ")}</dd>
          </div>
          <div>
            <dt>infra</dt>
            <dd>{skills.infra.join(" · ")}</dd>
          </div>
        </dl>
      </CodeCell>

      <CodeCell
        id="contact"
        n={11}
        {...bind("contact", "lab.contact()\n# internships, labs, Invar — I read mail")}
        code={
          <>
            lab.contact()
            {"\n"}
            <Cmt># internships, labs, Invar — I read mail</Cmt>
          </>
        }
      >
        <p className="contact-line">
          <a href={`mailto:${personalInfo.email}`}>{personalInfo.email}</a>
          <span aria-hidden="true"> · </span>
          <a href={`tel:+17253223944`}>{personalInfo.phone}</a>
        </p>
        <p className="contact-line">
          <a href={personalInfo.github} target="_blank" rel="noreferrer">
            GitHub
          </a>
          <span aria-hidden="true"> · </span>
          <a href={personalInfo.linkedin} target="_blank" rel="noreferrer">
            LinkedIn
          </a>
          <span aria-hidden="true"> · </span>
          <a href={personalInfo.scholar} target="_blank" rel="noreferrer">
            Scholar
          </a>
          <span aria-hidden="true"> · </span>
          <a href={personalInfo.orcid} target="_blank" rel="noreferrer">
            ORCID
          </a>
          <span aria-hidden="true"> · </span>
          <a href={personalInfo.cv} download>
            CV (PDF)
          </a>
        </p>
        <p className="contact-place">{personalInfo.location}</p>
      </CodeCell>
    </>
  );
}

function ProjectSheet({ project, bind, onOpen }) {
  if (!project) return null;
  const src = `from IPython.display import Image, display\ndisplay(Image("figures/${project.id}"))`;
  return (
    <>
      <MarkdownCell
        id={`p-${project.id}`}
        {...bind(
          `p-${project.id}`,
          `# ${project.title}\n\n${project.blurb}`
        )}
      >
        <p className="md-meta">
          projects/{project.id}.ipynb · {project.kind}
        </p>
        <h1 className="file-title">{project.title}</h1>
        <p className="lede">{project.blurb}</p>
      </MarkdownCell>

      {project.figure && (
        <CodeCell
          id={`fig-${project.id}`}
          n={1}
          {...bind(`fig-${project.id}`, src)}
          code={
            <>
              <Kw>from</Kw> IPython.display <Kw>import</Kw> Image, display
              {"\n"}
              display(Image(<Str>{`figures/${project.id}`}</Str>))
            </>
          }
        >
          <OutputFigure
            src={project.figure}
            alt={project.title}
            caption={project.figureCaption}
            href={project.href}
          />
        </CodeCell>
      )}

      <CodeCell
        id={`exp-${project.id}`}
        n={project.figure ? 2 : 1}
        {...bind(
          `exp-${project.id}`,
          `experiment("${project.id}", kind="${project.kind}")`
        )}
        code={
          <>
            experiment(<Str>{project.id}</Str>, kind=<Str>{project.kind}</Str>)
          </>
        }
      >
        <div className="exp">
          {project.points.length > 0 && (
            <ul>
              {project.points.map((pt) => (
                <li key={pt}>{pt}</li>
              ))}
            </ul>
          )}
          <p className="exp-links">
            {project.href && (
              <a href={project.href} target="_blank" rel="noreferrer">
                {project.href.replace(/^https?:\/\//, "")}
              </a>
            )}
            {project.repo && (
              <a href={project.repo} target="_blank" rel="noreferrer">
                repository
              </a>
            )}
            <button
              type="button"
              className="text-btn"
              onClick={() => onOpen(HOME_FILE)}
            >
              ← samarpan_mohanty.ipynb
            </button>
          </p>
        </div>
      </CodeCell>
    </>
  );
}

function PaperSheet({ paper, bind, onOpen }) {
  if (!paper) return null;
  const related =
    paper.id === "5psfj"
      ? { id: "omnigenome", name: "omnigenome.ipynb", kind: "project" }
      : paper.id === "g4rk2"
      ? { id: "kg", name: "kg.ipynb", kind: "project" }
      : paper.id === "liclust" || paper.id === "molytics-paper"
      ? { id: "molytics", name: "molytics.ipynb", kind: "project" }
      : paper.id === "sequence-paper"
      ? { id: "copolpred", name: "copolpred.ipynb", kind: "project" }
      : null;

  return (
    <>
      <MarkdownCell
        id={`paper-${paper.id}`}
        {...bind(`paper-${paper.id}`, `# ${paper.title}`)}
      >
        <p className="md-meta">
          papers/{paper.file} · {paper.venue} · {paper.year}
        </p>
        <h1 className="file-title">{paper.title}</h1>
        <p>{paper.authors}</p>
        {paper.note ? <p>{paper.note}</p> : null}
        <p className="exp-links">
          {paper.href ? (
            <a href={paper.href} target="_blank" rel="noreferrer">
              Open preprint
            </a>
          ) : (
            <span className="kind">in preparation</span>
          )}
          {related && (
            <button
              type="button"
              className="text-btn"
              onClick={() => onOpen(related)}
            >
              related · {related.name}
            </button>
          )}
        </p>
      </MarkdownCell>

      {paper.href && (
        <CodeCell
          id={`cite-${paper.id}`}
          n={1}
          {...bind(`cite-${paper.id}`, `lab.cite("${paper.id}")`)}
          code={
            <>
              lab.cite(<Str>{paper.id}</Str>)
            </>
          }
        >
          <p>
            <a href={paper.href} target="_blank" rel="noreferrer">
              {paper.href.replace(/^https?:\/\//, "")}
            </a>
          </p>
        </CodeCell>
      )}
    </>
  );
}

function TalksSheet({ bind }) {
  return (
    <MarkdownCell id="talks-md" {...bind("talks-md", "## Talks, posters, pitches")}>
      <p className="md-meta">talks.md</p>
      <h1 className="file-title">Talks, posters, pitches</h1>
      <TalkList />
    </MarkdownCell>
  );
}

function TalkList() {
  return (
    <>
      <ul className="talks">
        {talks.map((t) => (
          <li key={t.title}>
            <span className="kind">{t.kind}</span>
            <span className="when">{t.year}</span>
            <span>
              {t.title} — {t.where}
            </span>
          </li>
        ))}
      </ul>
      <h3 className="subh">Invar, out loud</h3>
      <ul className="talks">
        {ventures.map((v) => (
          <li key={v.title}>
            <span className="kind">pitch</span>
            <span className="when">{v.year}</span>
            <span>
              {v.title} — {v.where}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

export default Notebook;
